import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

export default function JourneyStart() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'validating' | 'valid' | 'invalid' | 'expired'>('validating');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setError('No token provided');
      return;
    }

    validateToken();
  }, [token, user]);

  const validateToken = async () => {
    try {
      // Check if token exists and is valid
      const { data: tokenData, error: tokenError } = await supabase
        .from('journey_entry_tokens')
        .select('*')
        .eq('token', token)
        .single();

      if (tokenError || !tokenData) {
        setStatus('invalid');
        setError('Invalid or unknown token');
        return;
      }

      // Check if expired
      if (new Date(tokenData.expires_at) < new Date()) {
        setStatus('expired');
        setError('This link has expired');
        return;
      }

      // Check if already used
      if (tokenData.used_at) {
        setStatus('invalid');
        setError('This link has already been used');
        return;
      }

      // Token is valid
      setStatus('valid');

      // If user is logged in, mark token as used and redirect
      if (user) {
        await supabase
          .from('journey_entry_tokens')
          .update({ 
            used_at: new Date().toISOString(),
            user_id: user.id 
          })
          .eq('id', tokenData.id);

        // Initialize journey for user
        await initializeJourney(user.id);

        // Redirect to journey
        navigate('/journey');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setStatus('invalid');
      setError('Failed to validate token');
    }
  };

  const initializeJourney = async (userId: string) => {
    // Get the first step
    const { data: firstStep } = await supabase
      .from('journey_steps')
      .select('id')
      .order('step_number')
      .limit(1)
      .single();

    if (firstStep) {
      // Create progress record for first step
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: firstStep.id,
          status: 'active',
          started_at: new Date().toISOString(),
        }, { onConflict: 'user_id,step_id' });
    }

    // Ensure journey state exists
    await supabase
      .from('user_journey_state')
      .upsert({
        user_id: userId,
        journey_completed: false,
      }, { onConflict: 'user_id' });
  };

  const handleLogin = () => {
    // Store token in session storage so we can use it after login
    if (token) {
      sessionStorage.setItem('journey_token', token);
    }
    navigate('/login');
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Welcome to Your Stewardship Journey</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === 'validating' && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Validating your access...</p>
            </div>
          )}

          {status === 'valid' && !user && (
            <div className="space-y-4">
              <div className="flex items-center justify-center text-green-600 mb-4">
                <CheckCircle2 className="w-6 h-6 mr-2" />
                <span>Access verified</span>
              </div>
              <p className="text-center text-muted-foreground">
                Please sign in to begin your journey.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={handleLogin} className="w-full">
                  Sign In
                </Button>
              </div>

            </div>
          )}

          {(status === 'invalid' || status === 'expired') && (
            <div className="space-y-4">
              <div className="flex items-center justify-center text-destructive mb-4">
                <AlertCircle className="w-6 h-6 mr-2" />
                <span>{error}</span>
              </div>
              <p className="text-center text-muted-foreground">
                {status === 'expired' 
                  ? 'Please contact support for a new access link.'
                  : 'Please check your link and try again.'}
              </p>
              <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
